import { z } from 'zod';
import { BANK_FEED_CALLBACK_PATH } from '../banking';
import type { PublicBranding } from './admin';
import type { PublicTenantInfo } from './security-policy';

// Phase I (I-C, I3): tenant hosts and custom portal domains.

/**
 * First labels of `<label>.<APP_DOMAIN>` that never name a tenant. Sign-up (I-A) refuses these as
 * company codes, so a tenant can never take over `domains.`, `www.` or the platform's own hosts.
 */
export const RESERVED_SUBDOMAINS = [
  'www',
  'app',
  'api',
  'admin',
  'platform',
  'domains',
  'status',
  'mail',
  'smtp',
  'imap',
  'pop',
  'ftp',
  'cdn',
  'static',
  'assets',
  'auth',
  'login',
  'sso',
  'billing',
  'help',
  'docs',
  'blog',
  'dev',
  'staging',
  'test',
  'demo',
  'localhost',
] as const;

export const isReservedSubdomain = (label: string) => (RESERVED_SUBDOMAINS as readonly string[]).includes(label.toLowerCase());

/** The TXT record name that proves control of a custom domain. */
export const verifyRecordName = (hostname: string) => `_denwen-msp-verify.${hostname}`;
/**
 * Rename compatibility: the TXT record name shown before the product became Denwen MSP. Domains set
 * up then keep that record, so verification and the periodic re-check accept it too.
 */
export const legacyVerifyRecordName = (hostname: string) => `_flowmsp-verify.${hostname}`;

export const DOMAIN_STATUSES = ['pending', 'verified', 'failed', 'removed'] as const;
export type DomainStatus = (typeof DOMAIN_STATUSES)[number];
export const DOMAIN_PURPOSES = ['portal'] as const;
export type DomainPurpose = (typeof DOMAIN_PURPOSES)[number];

const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * A fully qualified hostname such as `support.acme.co.uk`: at least three labels (a CNAME can't
 * sit on a bare registered domain), letters, digits and hyphens only, no IP addresses.
 */
export const customHostname = z
  .string()
  .trim()
  .toLowerCase()
  .transform((v) => v.replace(/\.$/, ''))
  .refine((v) => v.length <= 253, 'That hostname is too long')
  .refine((v) => v.split('.').every((l) => LABEL.test(l)), 'Enter a hostname such as support.example.com')
  .refine((v) => v.split('.').length >= 3, 'Use a subdomain such as support.example.com (a CNAME cannot be set on the bare domain)')
  .refine((v) => !/^\d+(\.\d+){3}$/.test(v), 'Enter a hostname, not an IP address');

export const customDomainInput = z.object({
  hostname: customHostname,
  purpose: z.enum(DOMAIN_PURPOSES).default('portal'),
});

export interface CustomDomainView {
  id: string;
  hostname: string;
  purpose: DomainPurpose;
  status: DomainStatus;
  lastCheckedAt: string | null;
  verifiedAt: string | null;
  lastError: string | null;
  createdAt: string;
  /** The exact DNS records the tenant must create. */
  records: { type: 'CNAME' | 'TXT'; name: string; value: string; found: boolean | null }[];
}

export interface DomainsOverview {
  /** Unset: host-based tenants are off on this server (no APP_DOMAIN). */
  appDomain: string | null;
  /** This tenant's own subdomain, e.g. acme.denwenmsp.app. */
  subdomain: string | null;
  cnameTarget: string | null;
  limit: number;
  /** Why this tenant cannot add a custom domain now (a free trial), or null. */
  addBlockedReason: string | null;
  domains: CustomDomainView[];
}

/**
 * GET /api/public/host: what the host the browser is on says about the tenant. On a tenant host
 * the sign-in page needs no company code, and shows the tenant's branding.
 */
export interface HostInfo {
  kind: 'app' | 'subdomain' | 'custom';
  tenant: PublicTenantInfo | null;
  branding: PublicBranding | null;
  /** On a custom portal domain: the tenant's staff app address (staff sign in there). */
  staffUrl: string | null;
}

// --- Integration OAuth on tenant hosts ---------------------------------------------------------
// Each provider knows one redirect URI: a web page on WEB_ORIGIN. A flow started on a tenant host
// adds "~<id>" to its `state`. Back on WEB_ORIGIN, where that admin is not signed in, the web app
// sends the browser to the API's relay, which hands the result to the same page on the tenant host.

/** The redirect page of each integration's OAuth flow (register `<WEB_ORIGIN><path>` with the provider). */
export const OAUTH_RETURN_PATHS = {
  hmrc: '/accounting/vat/hmrc-callback',
  m365: '/admin/microsoft-365',
  bank: BANK_FEED_CALLBACK_PATH,
  slack: '/admin/chat',
} as const;
export type OAuthReturnFlow = keyof typeof OAUTH_RETURN_PATHS;
export const OAUTH_RETURN_FLOWS = Object.keys(OAUTH_RETURN_PATHS) as OAuthReturnFlow[];
export const OAUTH_RETURN_SEPARATOR = '~';

/** For a provider's redirect landing on WEB_ORIGIN: the API relay to go to instead, or null. */
export function oauthRelayUrl(pathname: string, search: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const flow = OAUTH_RETURN_FLOWS.find((f) => OAUTH_RETURN_PATHS[f] === path);
  const raw = /(?:^|[?&])state=([^&]*)/.exec(search)?.[1];
  let state = '';
  try {
    state = raw ? decodeURIComponent(raw.replace(/\+/g, ' ')) : '';
  } catch {
    return null;
  }
  if (!flow || !state.includes(OAUTH_RETURN_SEPARATOR)) return null;
  return `/api/auth/oauth/return/${flow}${search.startsWith('?') ? search : `?${search}`}`;
}
